import { useEffect, useState } from 'react';
import {
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { twMerge } from 'tailwind-merge';

import { UserCompany } from 'models/user/UserCompany';
import { Button, Input, Label, TextArea } from 'common/materials';
import { useF1UserStatusRemark } from '../queries';

type Props = {
    isOpen: boolean;
    user?: UserCompany;
    onClose: () => void;
    onConfirm: (
        reason: string,
        inactiveWorkflowDate?: string,
        userStatusRemark?: string,
        inactiveWorkflow?: boolean,
        inactiveDms?: boolean
    ) => void | Promise<void>;
};

function pad(value: number) {
    return value.toString().padStart(2, '0');
}

function formatDateOnly(date: Date) {
    return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
}

function InactiveUserConfirmModal(props: Props) {
    const { isOpen, user, onClose, onConfirm } = props;

    const [reason, setReason] = useState('');
    const [inactiveWorkflowDate, setInactiveWorkflowDate] = useState<Date | null>(null);
    const [invalidFields, setInvalidFields] = useState<string[]>([]);

    const useWorkflow = user?.workflow_status === 'true';
    const useDms = user?.dms_status === 'true';

    const { data: f1UserStatus, isFetching: isFetchingF1UserStatus } = useF1UserStatusRemark({
        enabled: isOpen && !!user?.citizen_id,
        sid: user?.sid,
        platformId: user?.platform_id,
        username: user?.citizen_id
    });

    useEffect(() => {
        if (isOpen) {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            setInactiveWorkflowDate(yesterday);
        } else {
            setReason('');
            setInactiveWorkflowDate(null);
            setInvalidFields([]);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && f1UserStatus) {
            setReason(f1UserStatus.UserStatusRemark ?? '');
        }
    }, [isOpen, f1UserStatus]);

    const handleConfirm = () => {
        const emptyFields: string[] = [];

        if (!reason.trim()) {
            emptyFields.push('reason');
        }

        if (useWorkflow && !inactiveWorkflowDate) {
            emptyFields.push('inactiveWorkflowDate');
        }

        if (emptyFields.length > 0) {
            setInvalidFields(emptyFields);
            return;
        }

        onConfirm(
            reason.trim(),
            useWorkflow && inactiveWorkflowDate ? formatDateOnly(inactiveWorkflowDate) : undefined,
            user?.citizen_id ? reason.trim() : undefined,
            useWorkflow,
            useDms
        );
    };

    return (
        <Modal
            size="xl"
            isOpen={isOpen}
            onClose={onClose}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>ยืนยันการ Inactive ผู้ใช้งาน</ModalHeader>
                <ModalCloseButton />
                <ModalBody className="flex flex-col gap-4">
                    <div className="space-y-1.5">
                        <div className="flex items-center">
                            <Label>เหตุผล</Label>
                            <span className="ml-0.5 text-sm text-red-600">*</span>
                            {isFetchingF1UserStatus && (
                                <Spinner
                                    thickness="2px"
                                    speed="0.65s"
                                    color="#e55c00"
                                    size="sm"
                                    className="ml-2"
                                />
                            )}
                        </div>
                        <TextArea
                            value={reason}
                            onChange={event => {
                                setReason(event.target.value);
                                setInvalidFields(fields =>
                                    fields.filter(field => field !== 'reason')
                                );
                            }}
                            className={twMerge(
                                invalidFields.includes('reason') ? 'border-red-600' : ''
                            )}
                        />
                    </div>

                    {useWorkflow && (
                        <div className="space-y-1.5">
                            <p className="text-sm">
                                User <strong>{user?.citizen_id}</strong> จะถูก Inactive ที่ระบบ
                                Workflow
                            </p>
                            <div className="flex">
                                <Label>วันที่ Inactive</Label>
                                <span className="ml-0.5 text-sm text-red-600">*</span>
                            </div>
                            <DatePicker
                                selected={inactiveWorkflowDate}
                                onChange={date => {
                                    setInactiveWorkflowDate(date);
                                    setInvalidFields(fields =>
                                        fields.filter(field => field !== 'inactiveWorkflowDate')
                                    );
                                }}
                                dateFormat="dd/MM/yyyy"
                                customInput={
                                    <Input
                                        className={twMerge(
                                            invalidFields.includes('inactiveWorkflowDate')
                                                ? 'border-red-600'
                                                : ''
                                        )}
                                    />
                                }
                            />
                        </div>
                    )}

                    {useDms && (
                        <p className="text-sm">
                            User <strong>{user?.citizen_id}</strong> จะถูก Inactive ที่ระบบ DMS
                        </p>
                    )}
                </ModalBody>
                <ModalFooter className="gap-4">
                    <Button
                        outline
                        size="sm"
                        className="text-gray-400"
                        onClick={onClose}
                    >
                        ยกเลิก
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleConfirm}
                        className="bg-primary-900 text-white"
                    >
                        ยืนยัน
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default InactiveUserConfirmModal;
